import { ContextEventType, dataContext } from '../../FlowSync/context';
import { useCallback, useRef, useSyncExternalStore } from 'react';

// Single subscription manager to avoid multiple EventEmitter listeners
// All React components subscribe to this manager, which has one listener to dataContext
class ContextSubscriptionManager {
  private listeners = new Set<() => void>();
  private isSubscribed = false;

  subscribe(callback: () => void) {
    this.listeners.add(callback);

    // Subscribe to dataContext events only once
    if (!this.isSubscribed) {
      this.isSubscribed = true;
      dataContext.on(ContextEventType.CONTEXT_CHANGED, this.notifyListeners);
    }

    // Return cleanup function
    return () => {
      this.listeners.delete(callback);

      // Unsubscribe from dataContext if no more listeners
      if (this.listeners.size === 0 && this.isSubscribed) {
        this.isSubscribed = false;
        dataContext.off(ContextEventType.CONTEXT_CHANGED, this.notifyListeners);
      }
    };
  }

  private notifyListeners = () => {
    // Notify all React component subscribers
    this.listeners.forEach((callback) => callback());
  };
}

// Single shared instance
const contextSubscriptionManager = new ContextSubscriptionManager();

/**
 * Hook to access dataContext reactively using useSyncExternalStore
 * Mirrors all dataContext properties and adds project and computeNode
 */
export function useContext() {
  const snapshotRef = useRef<{
    user: typeof dataContext.user;
    workspace: typeof dataContext.workspace;
    activeEntity: typeof dataContext.activeEntity;
    activeEntityTypeId: typeof dataContext.activeEntityTypeId;
    workspaceTypeId: typeof dataContext.workspaceTypeId;
    userTypeId: typeof dataContext.userTypeId;
    localUser: typeof dataContext.localUser;
    cloudUser: typeof dataContext.cloudUser;
    currentUser: typeof dataContext.currentUser;
    visitorTypeId: typeof dataContext.visitorTypeId;
    someone: typeof dataContext.someone;
    activeOntology: typeof dataContext.activeOntology;
    activeLabels: typeof dataContext.activeLabels;
    manifests: typeof dataContext.manifests;
    plugins: typeof dataContext.plugins;
    project: typeof dataContext.project;
    computeNode: typeof dataContext.computeNode;
    sandboxComputeNode: typeof dataContext.sandboxComputeNode;
    visitor: typeof dataContext.visitor;
    bootstrapError: typeof dataContext.bootstrapError;
    isBootstrapping: typeof dataContext.isBootstrapping;
    cloudApiUrl: typeof dataContext.cloudApiUrl;
    cloudLoginAvailable: typeof dataContext.cloudLoginAvailable;
    cloudConnectionStatus: typeof dataContext.cloudConnectionStatus;
    desktopInfo: typeof dataContext.desktopInfo;
    runtimeKind: typeof dataContext.runtimeKind;
    isDesktop: typeof dataContext.isDesktop;
    version: typeof dataContext.version;
    instanceName: typeof dataContext.instanceName;
    warnings: typeof dataContext.warnings;
    agenticProcess: typeof dataContext.agenticProcess;
    agenticProcessTypeId: typeof dataContext.agenticProcessTypeId;
    activeShellId: typeof dataContext.activeShellId;
    activeTerminalTargetTypeId: typeof dataContext.activeTerminalTargetTypeId;
    workdir: typeof dataContext.workdir;
    snifferEnabled: typeof dataContext.snifferEnabled;
    snifferInstalled: typeof dataContext.snifferInstalled;
    snifferReady: typeof dataContext.snifferReady;
    terminalRuntimeError: typeof dataContext.terminalRuntimeError;
    isConnected: typeof dataContext.isConnected;
  }>({
    user: dataContext.user,
    workspace: dataContext.workspace,
    activeEntity: dataContext.activeEntity,
    activeEntityTypeId: dataContext.activeEntityTypeId,
    workspaceTypeId: dataContext.workspaceTypeId,
    userTypeId: dataContext.userTypeId,
    localUser: dataContext.localUser,
    cloudUser: dataContext.cloudUser,
    currentUser: dataContext.currentUser,
    visitorTypeId: dataContext.visitorTypeId,
    someone: dataContext.someone,
    activeOntology: dataContext.activeOntology,
    activeLabels: dataContext.activeLabels,
    manifests: dataContext.manifests,
    plugins: dataContext.plugins,
    project: dataContext.project,
    computeNode: dataContext.computeNode,
    sandboxComputeNode: dataContext.sandboxComputeNode,
    visitor: dataContext.visitor,
    bootstrapError: dataContext.bootstrapError,
    isBootstrapping: dataContext.isBootstrapping,
    cloudApiUrl: dataContext.cloudApiUrl,
    cloudLoginAvailable: dataContext.cloudLoginAvailable,
    cloudConnectionStatus: dataContext.cloudConnectionStatus,
    desktopInfo: dataContext.desktopInfo,
    runtimeKind: dataContext.runtimeKind,
    isDesktop: dataContext.isDesktop,
    version: dataContext.version,
    instanceName: dataContext.instanceName,
    warnings: dataContext.warnings,
    agenticProcess: dataContext.agenticProcess,
    agenticProcessTypeId: dataContext.agenticProcessTypeId,
    activeShellId: dataContext.activeShellId,
    activeTerminalTargetTypeId: dataContext.activeTerminalTargetTypeId,
    workdir: dataContext.workdir,
    snifferEnabled: dataContext.snifferEnabled,
    snifferInstalled: dataContext.snifferInstalled,
    snifferReady: dataContext.snifferReady,
    terminalRuntimeError: dataContext.terminalRuntimeError,
    isConnected: dataContext.isConnected,
  });

  // Subscribe to dataContext changes using the shared subscription manager
  // This ensures only ONE listener on the EventEmitter, regardless of how many components use this hook
  const subscribe = useCallback((callback: () => void) => {
    return contextSubscriptionManager.subscribe(callback);
  }, []);

  const getSnapshot = useCallback(() => {
    // Read current values from dataContext
    const current = {
      user: dataContext.user,
      workspace: dataContext.workspace,
      activeEntity: dataContext.activeEntity,
      activeEntityTypeId: dataContext.activeEntityTypeId,
      workspaceTypeId: dataContext.workspaceTypeId,
      userTypeId: dataContext.userTypeId,
      localUser: dataContext.localUser,
      cloudUser: dataContext.cloudUser,
      currentUser: dataContext.currentUser,
      visitorTypeId: dataContext.visitorTypeId,
      someone: dataContext.someone,
      activeOntology: dataContext.activeOntology,
      activeLabels: dataContext.activeLabels,
      manifests: dataContext.manifests,
      plugins: dataContext.plugins,
      project: dataContext.project,
      computeNode: dataContext.computeNode,
      sandboxComputeNode: dataContext.sandboxComputeNode,
      visitor: dataContext.visitor,
      bootstrapError: dataContext.bootstrapError,
      isBootstrapping: dataContext.isBootstrapping,
      cloudApiUrl: dataContext.cloudApiUrl,
      cloudLoginAvailable: dataContext.cloudLoginAvailable,
      cloudConnectionStatus: dataContext.cloudConnectionStatus,
      desktopInfo: dataContext.desktopInfo,
      runtimeKind: dataContext.runtimeKind,
      isDesktop: dataContext.isDesktop,
      version: dataContext.version,
      instanceName: dataContext.instanceName,
      warnings: dataContext.warnings,
      agenticProcess: dataContext.agenticProcess,
      agenticProcessTypeId: dataContext.agenticProcessTypeId,
      activeShellId: dataContext.activeShellId,
      activeTerminalTargetTypeId: dataContext.activeTerminalTargetTypeId,
      workdir: dataContext.workdir,
      snifferEnabled: dataContext.snifferEnabled,
      snifferInstalled: dataContext.snifferInstalled,
      snifferReady: dataContext.snifferReady,
      terminalRuntimeError: dataContext.terminalRuntimeError,
      isConnected: dataContext.isConnected,
    };

    // Only update snapshot if values have changed (to maintain stable reference)
    // For TypeId objects, compare by id instead of reference to avoid race conditions
    const prev = snapshotRef.current;
    const typeIdChanged = (
      a: { type?: string; id: string } | null | undefined,
      b: { type?: string; id: string } | null | undefined,
    ) => a?.type !== b?.type || a?.id !== b?.id;

    if (
      prev.user !== current.user ||
      prev.localUser !== current.localUser ||
      prev.cloudUser !== current.cloudUser ||
      prev.currentUser !== current.currentUser ||
      prev.workspace !== current.workspace ||
      prev.activeEntity !== current.activeEntity ||
      typeIdChanged(prev.activeEntityTypeId, current.activeEntityTypeId) ||
      typeIdChanged(prev.workspaceTypeId, current.workspaceTypeId) ||
      typeIdChanged(prev.userTypeId, current.userTypeId) ||
      typeIdChanged(prev.visitorTypeId, current.visitorTypeId) ||
      prev.someone !== current.someone ||
      prev.activeOntology !== current.activeOntology ||
      prev.activeLabels !== current.activeLabels ||
      prev.manifests !== current.manifests ||
      prev.plugins !== current.plugins ||
      prev.project !== current.project ||
      prev.computeNode !== current.computeNode ||
      prev.sandboxComputeNode !== current.sandboxComputeNode ||
      prev.visitor !== current.visitor ||
      prev.bootstrapError !== current.bootstrapError ||
      prev.isBootstrapping !== current.isBootstrapping ||
      prev.cloudApiUrl !== current.cloudApiUrl ||
      prev.cloudLoginAvailable !== current.cloudLoginAvailable ||
      prev.cloudConnectionStatus !== current.cloudConnectionStatus ||
      prev.desktopInfo !== current.desktopInfo ||
      prev.runtimeKind !== current.runtimeKind ||
      prev.isDesktop !== current.isDesktop ||
      prev.version !== current.version ||
      prev.instanceName !== current.instanceName ||
      prev.warnings !== current.warnings ||
      prev.agenticProcess !== current.agenticProcess ||
      typeIdChanged(prev.agenticProcessTypeId, current.agenticProcessTypeId) ||
      prev.activeShellId !== current.activeShellId ||
      typeIdChanged(prev.activeTerminalTargetTypeId, current.activeTerminalTargetTypeId) ||
      prev.workdir !== current.workdir ||
      prev.snifferEnabled !== current.snifferEnabled ||
      prev.snifferInstalled !== current.snifferInstalled ||
      prev.snifferReady !== current.snifferReady ||
      prev.terminalRuntimeError?.kind !== current.terminalRuntimeError?.kind ||
      prev.terminalRuntimeError?.processId !== current.terminalRuntimeError?.processId ||
      prev.isConnected !== current.isConnected
    ) {
      snapshotRef.current = current;
    }

    // Always return the same object reference (snapshotRef.current)
    // This is critical for useSyncExternalStore to work correctly
    return snapshotRef.current;
  }, []);

  // Subscribe to dataContext using useSyncExternalStore
  const contextData = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return contextData;
}
